/** @format */

import {
  Chain,
  Environment,
  SEOCategoryDetailsResponse,
  PrunedSEOCategoryDetails,
  PRUNED_SEO_CATEGORY_DETAILS_FIELDS,
} from '@shared/data-access/interfaces';
import { CustomHttpHeaders, MediaType } from '@shared/data-access/enums';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import {
  Injectable,
  PLATFORM_ID,
  TransferState,
  inject,
  makeStateKey,
  StateKey,
} from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, retry, tap } from 'rxjs/operators';
import { HttpService, KillswitchService, Logger } from '@shared/data-access/services';

@Injectable({
  providedIn: 'root',
})
export class SeoDao {
  private readonly chain = inject(Chain);
  private readonly environment = inject(Environment);
  protected platformId = inject(PLATFORM_ID);
  private readonly http = inject(HttpClient);
  private readonly state = inject(TransferState);
  private readonly httpService = inject(HttpService);
  private readonly logger = inject(Logger);
  private readonly killswitchService = inject(KillswitchService);
  private readonly isSearchApiBrandDomainEnabled = this.killswitchService.killSwitchForCurrentChain(
    'enableSearchApiBrandDomain',
  );

  private makeSEODataRequest$(
    seoToken: string,
    useDefaultHttpTransferCache: boolean,
    pageType: string,
    requestTimerStart: number,
  ) {
    const requestUrl = `${this.environment.api.seoByChain}seo-category/v1/categories?seoUrl=${seoToken}&storeId=${this.chain.storeId}&children=true&published=true`;
    // seoByChain is already always-on for this DAO; enableSearchApiBrandDomain only gates withCredentials
    let httpHeaders = new HttpHeaders({
      'Content-Type': MediaType.APPLICATION_JSON,
    });

    // Get Origin Host Header
    const origin = this.httpService.getRequestHeaderValueAndSetStateKey('ORIGIN_HOST');
    if (origin) {
      httpHeaders = httpHeaders.set(CustomHttpHeaders.ORIGIN, origin);
    }

    return this.http
      .get<SEOCategoryDetailsResponse>(requestUrl, {
        headers: httpHeaders,
        withCredentials: this.isSearchApiBrandDomainEnabled(),
        transferCache: useDefaultHttpTransferCache,
      })
      .pipe(
        tap(() => {
          const executionTime = Date.now().valueOf() - requestTimerStart;
          this.logger.log(
            `Execution time for - SEO API Request for ${this.chain.storeId}, ${pageType}/${seoToken} - ${executionTime}ms`,
          );
        }),
      );
  }

  private getCachedSEOData$<T>(
    stateKey: StateKey<T>,
    seoToken: string,
    pageType: string,
    requestTimerStart: number,
  ): Observable<T> | null {
    const element = this.state.get(stateKey, null);

    // Check to see if state exists
    if (element) {
      const executionTime = Date.now().valueOf() - requestTimerStart;
      this.logger.log(
        `Execution time for - Cached SEO API Request for ${this.chain.storeId}, ${pageType}/${seoToken} - ${executionTime}ms`,
      );

      return of(element);
    }

    return null;
  }

  /**
   * Retrieves SEO data based on the provided SEO token.
   *
   * @param seoToken - The token used to identify the SEO data.
   * @param retries - The number of retry attempts for the HTTP request (default is 0).
   * @param useDefaultHttpTransferCache - Flag to determine whether to use the default angular's HTTP transferState cache (default is false).
   * @param pageType - The type of the page for which SEO data is being requested (for logging purposes.)
   * @returns An Observable that emits the SEO data.
   */
  getSEOData$(
    seoToken: string,
    retries = 0,
    useDefaultHttpTransferCache = false,
    pageType = '',
  ): Observable<SEOCategoryDetailsResponse> {
    const requestTimerStart = Date.now().valueOf();
    const ELEMENT_KEY = makeStateKey<SEOCategoryDetailsResponse>(`seo-response-${seoToken}`);
    if (!useDefaultHttpTransferCache) {
      const cachedElement = this.getCachedSEOData$(
        ELEMENT_KEY,
        seoToken,
        pageType,
        requestTimerStart,
      );

      if (cachedElement) {
        return cachedElement;
      }
    }

    return this.makeSEODataRequest$(
      seoToken,
      useDefaultHttpTransferCache,
      pageType,
      requestTimerStart,
    ).pipe(
      tap((response) => {
        if (!useDefaultHttpTransferCache) {
          this.state.set(ELEMENT_KEY, response);
        }
      }),
      retry(retries),
    );
  }

  /**
   * Retrieves and caches the SEO category data for the given SEO token, with only the necessary subset of fields.
   * This is currently specifically meant for use by PLP.
   * A separate method is necessary for type safety until conditional return types are possible (see https://github.com/microsoft/TypeScript/issues/33912)
   */
  getPrunedSEOCategory$(seoToken: string, retries = 0, pageType = '') {
    const requestTimerStart = Date.now().valueOf();
    const ELEMENT_KEY = makeStateKey<PrunedSEOCategoryDetails>(`seo-category-${seoToken}`);

    const cachedElement = this.getCachedSEOData$(
      ELEMENT_KEY,
      seoToken,
      pageType,
      requestTimerStart,
    );

    if (cachedElement) {
      return cachedElement;
    }

    return this.makeSEODataRequest$(seoToken, false, pageType, requestTimerStart).pipe(
      map((response) => {
        const categoryDetails = response.categories[0];
        if (!categoryDetails) {
          return null;
        }
        // The `as` cast here is necessary if we want to preserve a single source of truth for the list of fields
        return Object.fromEntries(
          PRUNED_SEO_CATEGORY_DETAILS_FIELDS.map((field: keyof PrunedSEOCategoryDetails) => [
            field,
            categoryDetails[field],
          ]),
        ) as PrunedSEOCategoryDetails;
      }),
      tap((response) => {
        this.state.set(ELEMENT_KEY, response);
      }),
      retry(retries),
    );
  }
}
